import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { FileText, TrendUp, ChartBar } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { getQuotesConversionStats } from '../services/api';

const TERR_LABELS = {
  FR: 'Métropole', GP: 'Guadeloupe', MQ: 'Martinique', RE: 'Réunion', GF: 'Guyane',
  YT: 'Mayotte', PM: 'St-Pierre', BL: 'St-Barth', MF: 'St-Martin',
  NC: 'Nouvelle-Calédonie', PF: 'Polynésie', WF: 'Wallis', TF: 'TAAF', 'N/A': 'Non renseigné',
};

function fmtEur(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export function QuotesGlobalKpi({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getQuotesConversionStats().then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Card className="border-[#E8E6E1]"><CardContent className="p-4"><Loader2 className="animate-spin" /></CardContent></Card>;
  if (!data) return null;
  const g = data.global;
  return (
    <Card className="border-[#E8E6E1]" data-testid="quotes-global-kpi">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={16} className="text-[#2D5A4C]" />
          <h3 className="text-xs uppercase tracking-wider text-[#5E6D66] font-semibold">Devis → Facture</h3>
        </div>
        <div className={`${compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 lg:grid-cols-4 gap-4'} [&>div]:min-w-0 [&>div]:break-words`}>
          <div>
            <p className="text-[10px] uppercase text-[#5E6D66]">Émis</p>
            <p className="text-2xl font-bold text-[#1A2421]">{g.total}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-[#5E6D66]">Taux acceptation</p>
            <p className="text-2xl font-bold text-blue-700">{g.acceptance_rate}%</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-[#5E6D66]">Taux conversion (payés)</p>
            <p className="text-2xl font-bold text-emerald-700">{g.conversion_rate}%</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-[#5E6D66]">CA TTC converti</p>
            <p className="text-2xl font-bold text-[#2D5A4C]">{fmtEur(g.ca_ttc)}</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#E8E6E1] flex gap-2 flex-wrap text-[10px]">
          <Badge className="bg-blue-100 text-blue-800">Envoyés {g.sent}</Badge>
          <Badge className="bg-emerald-100 text-emerald-800">Acceptés {g.accepted}</Badge>
          <Badge className="bg-emerald-600 text-white">Payés {g.paid}</Badge>
          <Badge className="bg-red-100 text-red-800">Refusés {g.declined}</Badge>
          <Badge className="bg-amber-100 text-amber-800">Expirés {g.expired}</Badge>
        </div>
      </CardContent>
    </Card>
  );
}

export function QuotesByTerritoryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = () => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    getQuotesConversionStats(params).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, [dateFrom, dateTo]);

  if (loading) return <Card className="border-[#E8E6E1]"><CardContent className="p-4"><Loader2 className="animate-spin" /></CardContent></Card>;
  if (!data || (data.items || []).length === 0) {
    return (
      <Card className="border-[#E8E6E1]" data-testid="quotes-by-territory-panel">
        <CardContent className="p-6 text-center text-[#5E6D66]">
          <ChartBar size={28} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">Aucun devis dans la période sélectionnée.</p>
        </CardContent>
      </Card>
    );
  }
  const maxCA = Math.max(1, ...data.items.map((i) => i.ca_ttc));

  return (
    <Card className="border-[#E8E6E1]" data-testid="quotes-by-territory-panel">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <TrendUp size={16} className="text-[#2D5A4C]" /> Conversion devis par territoire
          </h3>
          <div className="flex flex-wrap gap-2 items-end max-w-full">
            <div className="min-w-0 max-w-full">
              <Label className="text-[10px]">Du</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs border-[#E8E6E1]" data-testid="quotes-stats-from" />
            </div>
            <div className="min-w-0 max-w-full">
              <Label className="text-[10px]">Au</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs border-[#E8E6E1]" data-testid="quotes-stats-to" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E1] text-left text-[10px] uppercase tracking-wider text-[#5E6D66]">
                <th className="py-2 pr-3">Territoire</th>
                <th className="py-2 pr-3 text-right">Émis</th>
                <th className="py-2 pr-3 text-right">Envoyés</th>
                <th className="py-2 pr-3 text-right">Acceptés</th>
                <th className="py-2 pr-3 text-right">Payés</th>
                <th className="py-2 pr-3 text-right">Refusés</th>
                <th className="py-2 pr-3 text-right">Expirés</th>
                <th className="py-2 pr-3 text-right">Conversion</th>
                <th className="py-2 pr-3">CA TTC converti</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.territoire} className="border-b border-[#E8E6E1]" data-testid={`quotes-stats-row-${row.territoire}`}>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-[10px]">{row.territoire}</Badge>
                      <span className="text-xs">{TERR_LABELS[row.territoire] || row.territoire}</span>
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right font-medium">{row.total}</td>
                  <td className="py-2 pr-3 text-right text-blue-700">{row.sent}</td>
                  <td className="py-2 pr-3 text-right text-emerald-700">{row.accepted}</td>
                  <td className="py-2 pr-3 text-right font-semibold text-emerald-800">{row.paid}</td>
                  <td className="py-2 pr-3 text-right text-red-700">{row.declined}</td>
                  <td className="py-2 pr-3 text-right text-amber-700">{row.expired}</td>
                  <td className="py-2 pr-3 text-right">
                    <Badge className={row.conversion_rate >= 50 ? 'bg-emerald-100 text-emerald-800' : row.conversion_rate >= 20 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}>
                      {row.conversion_rate}%
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 min-w-[160px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-[#F0EFEB] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#2D5A4C] rounded-full transition-all"
                          style={{ width: `${(row.ca_ttc / maxCA) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono w-20 text-right">{fmtEur(row.ca_ttc)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
